import React__default from 'react';
import { Path, Element, Transforms, Editor } from 'slate';
import { serialize } from '../../../../core/utilities/serialize/serialize.mjs';
import { getRenderElement } from '../../../../core/rendering/getRenderElement.mjs';
import { createElement } from '../../../../core/utilities/nodes/elements/createElement.mjs';
import { isBlock } from '../../../../core/queries/isBlock.mjs';
import { isInline } from '../../../../core/queries/isInline.mjs';
import { getDerivedAttributes, getDerivedDataFromHTML } from '../../../../core/utilities/side-effects/combineSideEffects.mjs';
import { getElementDeserializer } from '../../../../core/serialization/deserialize/utilities/getElementDeserializer.mjs';
import { Paragraph } from './components/Paragraph/Paragraph.mjs';
import { isTextNode } from '../../../../core/queries/isTextNode.mjs';
import { isElement } from '../../../../core/utilities/nodes/elements/isElement.mjs';

const Type = 'paragraph';
const element = 'p';
const emptyParagraph = serialize('p', '');
const ParagraphPlugin = options => ({
  type: Type,
  renderElement: getRenderElement({
    type: Type,
    rootProps: {},
    component(props) {
      const attributes = getDerivedAttributes({
        sideEffects: options === null || options === void 0 ? void 0 : options.sideEffects,
        element: props.element
      });
      return /*#__PURE__*/React__default.createElement(Paragraph, Object.assign({}, props, {
        style: attributes.style
      }));
    }
  }),
  extendEditor(editor) {
    const {
      normalizeNode
    } = editor;
    const isInlineOrText = node => isTextNode(node) || isInline(editor, node);
    const isWhitespace = node => isTextNode(node) && node.text.trim() === '';
    editor.normalizeNode = entry => {
      const [node, path] = entry;

      // Ensure document element doesn't contain text nodes that are not wrapped in a paragraph
      if (Path.equals(path, [0]) && Element.isElement(node)) {
        const {
          children
        } = node;
        const hasOrphanedWhitespace = children.some(isWhitespace);
        if (hasOrphanedWhitespace) {
          // Remove orphaned empty text (whitespace, line breaks)
          Transforms.removeNodes(editor, {
            at: [0],
            match: node => isWhitespace(node) && children.includes(node)
          });
          return;
        }
        const hasOrphanedInlineOrTextNodes = children.some(isInlineOrText);
        if (hasOrphanedInlineOrTextNodes) {
          Editor.withoutNormalizing(editor, () => {
            let pathStart = null;
            const ranges = [];

            // Wrap remaining orphaned elements in Paragraph element
            for (let i = 0; i < node.children.length; i++) {
              const child = node.children[i];
              if (child && isInlineOrText(child)) {
                if (pathStart == null) {
                  pathStart = i;
                }
                const nextChild = node.children[i + 1];
                if (!nextChild || !isInlineOrText(nextChild)) {
                  const range = Editor.range(editor, [0, pathStart], [0, i]);
                  ranges.push(Editor.rangeRef(editor, range));
                  pathStart = null;
                }
              }
            }
            if (ranges.length > 0) {
              for (const rangeRef of ranges) {
                if (rangeRef.current) {
                  Transforms.wrapNodes(editor, createElement(Type), {
                    at: rangeRef.current,
                    match: isInlineOrText
                  });
                }
                rangeRef.unref();
              }
              return;
            }
            const document = editor.children[0];

            // Unwrap any nested Blocks
            Transforms.liftNodes(editor, {
              at: [0],
              match: node => isBlock(editor, node) && !document.children.includes(node) && children.includes(node)
            });
          });
          return;
        }
      }

      // Ensure only inlines or text nodes are nested within paragraph nodes
      if (isElement(node) && !isInline(editor, node)) {
        const paragraphAncestor = Editor.above(editor, {
          at: path,
          match: isParagraph
        });
        if (paragraphAncestor) {
          Transforms.liftNodes(editor, {
            at: path
          });
          return;
        }
      }

      // Fall back to the original `normalizeNode` to enforce other constraints.
      normalizeNode(entry);
    };
    return editor;
  },
  serialize: {
    after(html) {
      return html === emptyParagraph ? '' : html;
    },
    element(props) {
      const attributes = getDerivedAttributes({
        sideEffects: options === null || options === void 0 ? void 0 : options.sideEffects,
        element: props.element
      });
      return serialize('p', props.children, attributes);
    }
  },
  deserialize: {
    before(html) {
      return html === '' ? emptyParagraph : html;
    },
    element: getElementDeserializer({
      type: Type,
      rules: [{
        nodeNames: element.toUpperCase()
      }],
      node: el => {
        const paragraphNodeData = {
          type: Type
        };
        return getDerivedDataFromHTML({
          sideEffects: options === null || options === void 0 ? void 0 : options.sideEffects,
          data: paragraphNodeData,
          htmlElement: el
        });
      }
    })
  }
});
function isParagraph(node) {
  return isElement(node) && node.type === Type;
}

export { ParagraphPlugin, Type };
